import { useState, useEffect, type ReactNode } from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { shortenAddress } from '@gonka-wallet/crypto';
import {
  SegmentedControl,
  StatTile,
  Sparkline,
  SignalBars,
  Badge,
} from '@gonka-wallet/ui-tokens';
import type { MinerStats, RewardEntry } from '../../lib/minerApi.js';
import { formatConfirmationRate } from '../../lib/cpoc.js';
import { useMinerRewards } from '../../hooks/useMinerRewards.js';
import { useMinerHardware } from '../../hooks/useMinerHardware.js';
import { useMinerCollateral } from '../../hooks/useMinerCollateral.js';
import { WalletGlyph } from '../WalletGlyph.js';
import { formatWeight, formatGNK } from './minerFormat.js';
import { WeightHero } from './WeightHero.js';
import { StateStrip } from './StateStrip.js';
import { EpochHistoryList } from './EpochHistoryList.js';

export type MinerDetailTab = 'details' | 'hardware' | 'rewards' | 'collateral';

const ALL_TABS: readonly MinerDetailTab[] = ['details', 'hardware', 'rewards', 'collateral'];

interface Props {
  readonly address: string | null;
  readonly minerStats: MinerStats | null;
  readonly currentEpoch?: number | null;
  readonly initialTab?: MinerDetailTab;
  readonly onClose: () => void;
}

/**
 * Health indicator for the bottom-sheet header. Four bars scale based on
 * missed %, invalid %, and is_healthy/is_jailed flags from MinerStats.
 * Returned tone drives the bar color so the header conveys severity at
 * a glance without the user having to read the State strip inside the
 * Details tab.
 */
function computeHealth(m: MinerStats | null): {
  readonly bars: number;
  readonly tone: 'pos' | 'warn' | 'neg' | 'muted';
  readonly reason: string;
} {
  if (!m) return { bars: 0, tone: 'muted', reason: '' };
  if (!m.is_active) {
    return { bars: 0, tone: 'muted', reason: 'Inactive' };
  }
  if (m.is_jailed) {
    return { bars: 0, tone: 'neg', reason: 'Jailed — no rewards' };
  }
  if (m.is_healthy === false) {
    return { bars: 1, tone: 'neg', reason: 'Node unhealthy' };
  }
  const miss = m.missed_percent ?? 0;
  const invalid = m.invalid_percent ?? 0;
  const breakdown = `miss ${miss.toFixed(1)}% · invalid ${invalid.toFixed(1)}%`;
  if (miss > 10 || invalid > 1) return { bars: 1, tone: 'neg', reason: `Poor · ${breakdown}` };
  if (miss > 5 || invalid > 0.5) return { bars: 2, tone: 'warn', reason: `Mediocre · ${breakdown}` };
  if (miss > 1) return { bars: 3, tone: 'pos', reason: `Good · ${breakdown}` };
  return { bars: 4, tone: 'pos', reason: `Excellent · ${breakdown}` };
}

function healthColor(tone: 'pos' | 'warn' | 'neg' | 'muted'): string {
  if (tone === 'pos') return 'var(--color-pos)';
  if (tone === 'warn') return 'var(--color-warning)';
  if (tone === 'neg') return 'var(--color-neg)';
  return 'var(--color-text-tertiary)';
}

export function MinerDetailModal(props: Props): ReactNode {
  const dragControls = useDragControls();
  const { address, minerStats, currentEpoch, initialTab = 'details', onClose } = props;
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<MinerDetailTab>(initialTab);
  const [copied, setCopied] = useState(false);

  const [rewardsPage, setRewardsPage] = useState(1);
  const [accumulatedRewards, setAccumulatedRewards] = useState<RewardEntry[]>([]);

  const rewardsQuery = useMinerRewards(
    activeTab === 'rewards' || activeTab === 'details' ? address : null,
    rewardsPage,
  );
  const hardwareQuery = useMinerHardware(activeTab === 'hardware' ? address : null);
  const collateralQuery = useMinerCollateral(activeTab === 'collateral' ? address : null);

  useEffect(() => {
    setAccumulatedRewards([]);
    setRewardsPage(1);
  }, [address]);

  useEffect(() => {
    if (rewardsQuery.data?.rewards) {
      setAccumulatedRewards((prev) => {
        const existing = new Set(prev.map((r) => r.epoch_id));
        const merged = [...prev];
        for (const r of rewardsQuery.data!.rewards) {
          if (!existing.has(r.epoch_id)) merged.push(r);
        }
        return merged.sort((a, b) => b.epoch_id - a.epoch_id);
      });
    }
  }, [rewardsQuery.data]);

  const onCopyAddress = () => {
    if (!address) return;
    navigator.clipboard?.writeText(address).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tabLabel = (tab: MinerDetailTab): string => {
    switch (tab) {
      case 'details': return t('miner.tabDetails');
      case 'hardware': return t('miner.tabHardware');
      case 'rewards': return t('miner.tabRewards');
      case 'collateral': return t('miner.tabCollateral');
    }
  };

  if (!address) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black/40"
        role="dialog"
        aria-modal="true"
      />
      <motion.div
        key="sheet"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 360, damping: 36 }}
        drag="y"
        dragListener={false}
        dragControls={dragControls}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.6 }}
        dragMomentum={false}
        onDragEnd={(_, { offset, velocity }) => {
          if (offset.y > 120 || velocity.y > 500) onClose();
        }}
        onClick={(e) => e.stopPropagation()}
        className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[88vh] max-w-[480px] flex-col rounded-t-[20px] bg-surface-1 shadow-[0_-10px_40px_rgba(0,0,0,0.2)]"
      >
        <div
          onPointerDown={(event) => dragControls.start(event)}
          className="flex shrink-0 cursor-grab touch-none justify-center px-5 pb-1 pt-3 active:cursor-grabbing"
        >
          <div className="h-1 w-9 rounded-full bg-border-strong" aria-hidden />
        </div>
        <div className="overflow-auto px-5 pb-5">

        {/* Header */}
        <div className="grid grid-cols-[40px_1fr_auto] items-center gap-3">
          <WalletGlyph seed={address} size={40} />
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold tracking-[-0.2px] text-text-primary">
              {minerStats?.moniker && minerStats.moniker.trim().length > 0
                ? minerStats.moniker
                : minerStats?.rank != null
                  ? `${t('miner.node')} #${minerStats.rank}`
                  : shortenAddress(address)}
            </div>
            <div className="mt-0.5 flex items-center gap-1 font-mono text-[10px] text-text-tertiary">
              <span className="truncate">{shortenAddress(address)}</span>
              <CopyButton onClick={onCopyAddress} copied={copied} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(() => {
              const h = computeHealth(minerStats);
              return (
                <span title={h.reason} aria-label={`Health: ${h.reason || 'unknown'}`}>
                  <SignalBars lit={h.bars} total={4} color={healthColor(h.tone)} />
                </span>
              );
            })()}
            <Badge dot>
              {minerStats?.is_active ? t('miner.statusActive') : t('miner.statusDown')}
            </Badge>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-8 w-8 items-center justify-center rounded-full text-text-tertiary hover:bg-surface-2"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="mt-4">
          <SegmentedControl
            ariaLabel="Miner detail tabs"
            value={activeTab}
            onChange={(v) => setActiveTab(v as MinerDetailTab)}
            options={ALL_TABS}
            renderLabel={tabLabel}
            className="w-full"
          />
        </div>

        {/* Tab content */}
        <div className="mt-4">
          {activeTab === 'details' && (
            <DetailsTab stats={minerStats} rewards={rewardsQuery.data ?? null} />
          )}
          {activeTab === 'hardware' && (
            <HardwareTab data={hardwareQuery.data ?? null} />
          )}
          {activeTab === 'rewards' && (
            <RewardsTab
              address={address}
              data={{
                rewards: accumulatedRewards,
                pagination: rewardsQuery.data?.pagination ?? { has_next: false },
              }}
              epochId={currentEpoch ?? null}
              loadNextPage={() => setRewardsPage((p) => p + 1)}
            />
          )}
          {activeTab === 'collateral' && (
            <CollateralTabView data={collateralQuery.data ?? null} />
          )}
        </div>

        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function CopyButton({ onClick, copied }: { onClick: () => void; copied: boolean }): ReactNode {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={copied ? 'Copied' : 'Copy'}
      className="inline-grid h-4 w-4 place-items-center text-current hover:text-text-primary"
    >
      {copied ? (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
          <path
            d="M2.5 6.5 L5 9 L9.5 3.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
          <rect
            x="3.5"
            y="3.5"
            width="6"
            height="6"
            rx="1.2"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M2 7.5 V2.8 A0.8 0.8 0 0 1 2.8 2 H7.5"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
}

function DetailsTab({
  stats,
  rewards,
}: {
  stats: MinerStats | null;
  rewards: { rewards: readonly RewardEntry[] } | null;
}): ReactNode {
  const { t } = useTranslation();

  const recentRewards = (rewards?.rewards ?? []).slice(0, 30);
  const weightSeries = [...recentRewards]
    .reverse()
    .map((r) => r.weight)
    .filter((w): w is number => w != null);

  const confirmationDisplay = formatConfirmationRate(stats?.confirmation_rate);
  const legacyConfirmationRatio = stats?.confirmation_ratio ?? null;
  const confirmationValue =
    confirmationDisplay.value ?? legacyConfirmationRatio ?? '—';
  const confirmationSubLabel =
    confirmationDisplay.subLabel != null ? t(confirmationDisplay.subLabel) : null;
  const weightToConfirm = stats?.weight_to_confirm ?? null;

  const inferenceCount = stats?.inference_count ?? 0;
  const missedRequests = stats?.missed_requests ?? 0;
  const inferenceValid = stats?.validated_inferences ?? 0;
  const inferenceInvalid = stats?.invalidated_inferences ?? 0;
  const totalAttempts = inferenceCount + missedRequests;
  const missRate = totalAttempts > 0 ? (missedRequests / totalAttempts) * 100 : 0;
  const successRate = totalAttempts > 0 ? (inferenceCount / totalAttempts) * 100 : 0;

  return (
    <div>
      <WeightHero stats={stats} weightSeries={weightSeries} />

      <StateStrip
        isHealthy={stats?.is_healthy ?? null}
        isJailed={stats?.is_jailed ?? null}
        missedPercent={stats?.missed_percent ?? null}
      />

      {/* Reliability strip */}
      <div className="mb-3.5 grid grid-cols-3 gap-2">
        <ReliabilityTile
          label={t('miner.miss')}
          value={stats ? `${stats.missed_percent.toFixed(1)}%` : '—'}
          tone={stats && stats.missed_percent >= 5 ? 'warn' : undefined}
        />
        <ReliabilityTile
          label={t('miner.invalidSlash')}
          value={stats ? `${stats.invalid_percent.toFixed(1)}%` : '—'}
          tone={
            stats == null
              ? undefined
              : stats.invalid_percent >= 1
                ? 'neg'
                : stats.invalid_percent === 0
                  ? 'pos'
                  : undefined
          }
        />
        <ReliabilityTile
          label={t('minerDetail.gpus')}
          value={stats ? `${stats.gpus}` : '—'}
        />
      </div>

      {/* Confirmation strip — always rendered, em-dash when missing */}
      <div className="mb-3.5 grid grid-cols-2 gap-2">
        <ReliabilityTile
          label={t('minerDetail.confirmationRatio')}
          value={confirmationValue}
          sub={confirmationSubLabel ?? undefined}
          tone={confirmationDisplay.tone}
        />
        <ReliabilityTile
          label={t('minerDetail.weightToConfirm')}
          value={weightToConfirm != null ? formatWeight(weightToConfirm) : '—'}
        />
      </div>

      {/* Inference Stats */}
      <div className="mb-3.5 rounded-2xl bg-surface-2 px-3.5 pt-3 pb-3.5">
        <div className="mb-2.5 text-[10px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
          {t('minerDetail.inferenceStats')}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <InferenceTile
            label={t('minerDetail.totalInferences')}
            value={totalAttempts.toLocaleString()}
          />
          <InferenceTile
            label={t('minerDetail.missedRequests')}
            value={missedRequests.toLocaleString()}
            tone={missedRequests > 0 ? 'warn' : undefined}
          />
          <InferenceTile
            label={t('minerDetail.validatedInferences')}
            value={inferenceValid.toLocaleString()}
            tone={inferenceValid > 0 ? 'pos' : undefined}
          />
          <InferenceTile
            label={t('minerDetail.invalidatedInferences')}
            value={inferenceInvalid.toLocaleString()}
            tone={inferenceInvalid > 0 ? 'neg' : undefined}
          />
          <InferenceTile
            label={t('minerDetail.missPercent')}
            value={`${missRate.toFixed(1)}%`}
            tone={missRate > 5 ? 'warn' : undefined}
          />
          <InferenceTile
            label={t('minerDetail.successPercent')}
            value={`${successRate.toFixed(1)}%`}
            tone={totalAttempts > 0 ? 'pos' : undefined}
          />
        </div>
      </div>

      {/* Models — keep existing simple pill list */}
      {stats?.models && stats.models.length > 0 && (
        <div className="rounded-2xl bg-surface-2 px-3.5 py-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
            {t('minerDetail.models')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {stats.models.map((m) => (
              <span key={m} className="rounded-md bg-surface-3 px-2.5 py-1 font-mono text-[11px] text-text-secondary">
                {m}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReliabilityTile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'pos' | 'neg' | 'warn';
}): ReactNode {
  const cls =
    tone === 'pos' ? 'text-pos' : tone === 'neg' ? 'text-neg' : tone === 'warn' ? 'text-warning' : 'text-text-primary';
  return (
    <div className="rounded-xl bg-surface-2 px-3 py-2.5">
      <div className="mb-1 text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">{label}</div>
      <div className={`font-mono text-base font-semibold ${cls}`}>{value}</div>
      {sub && (
        <div className="mt-0.5 text-[9px] font-medium uppercase tracking-[1px] text-text-tertiary">
          {sub}
        </div>
      )}
    </div>
  );
}

function InferenceTile({ label, value, tone }: { label: string; value: string; tone?: 'pos' | 'neg' | 'warn' }): ReactNode {
  const cls =
    tone === 'pos' ? 'text-pos'
    : tone === 'neg' ? 'text-neg'
    : tone === 'warn' ? 'text-warning'
    : 'text-text-primary';
  return (
    <div className="rounded-xl bg-surface-3 px-2.5 py-2">
      <div className="mb-0.5 text-[9px] font-semibold uppercase tracking-[1.1px] text-text-tertiary">{label}</div>
      <div className={`font-mono text-sm font-semibold ${cls}`}>{value}</div>
    </div>
  );
}

function HardwareTab({
  data,
}: {
  data: {
    hardware_nodes: readonly {
      local_id: string;
      status: string;
      hardware?: readonly { type: string; count: number }[];
      poc_weight?: number;
    }[];
  } | null;
}): ReactNode {
  const { t } = useTranslation();
  if (!data) {
    return <div className="py-6 text-center text-[11px] text-text-tertiary">…</div>;
  }

  const nodes = data.hardware_nodes;
  const totalNodes = nodes.length;
  const activeNodes = nodes.filter((n) => n.status !== 'FAILED').length;
  const totalGpus = nodes.reduce(
    (sum, n) => sum + (n.hardware ?? []).reduce((s, h) => s + h.count, 0),
    0,
  );

  const gpuMap = new Map<string, number>();
  for (const node of nodes) {
    for (const h of node.hardware ?? []) {
      gpuMap.set(h.type, (gpuMap.get(h.type) ?? 0) + h.count);
    }
  }
  const gpuAggregate = [...gpuMap.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([type, count]) => `${count}× ${type}`)
    .join(' · ');

  if (totalNodes === 0) {
    return (
      <div className="rounded-2xl bg-surface-2 px-4 py-10 text-center">
        <div className="text-xs text-text-secondary">{t('miner.hwEmpty')}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary strip */}
      <div className="mb-3.5 grid grid-cols-3 overflow-hidden rounded-2xl bg-surface-2">
        <SummaryCell label={t('miner.hwNodes')} value={`${totalNodes}`} />
        <SummaryCell
          label={t('miner.hwActive')}
          value={`${activeNodes}`}
          tone={activeNodes === totalNodes ? 'pos' : activeNodes === 0 ? 'neg' : 'warn'}
        />
        <SummaryCell label={t('minerDetail.gpus')} value={`${totalGpus}`} last />
      </div>

      {/* Aggregate GPU line (if any hardware registered) */}
      {gpuAggregate && (
        <div className="mb-3.5 rounded-2xl bg-surface-2 px-3.5 py-3">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
            {t('miner.hardware')}
          </div>
          <div className="font-mono text-sm text-text-primary">{gpuAggregate}</div>
        </div>
      )}

      {/* Per-node list */}
      <div className="mb-2 flex items-baseline justify-between px-0.5">
        <div className="text-[11px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
          {t('miner.hwNodesHeader')}
        </div>
        <div className="font-mono text-[11px] text-text-tertiary">{totalNodes}</div>
      </div>
      <div className="space-y-2">
        {nodes.map((node, idx) => (
          <HardwareNodeCard key={node.local_id || idx} node={node} fallbackIdx={idx} />
        ))}
      </div>
    </div>
  );
}

function SummaryCell({
  label,
  value,
  tone,
  last = false,
}: {
  label: string;
  value: string;
  tone?: 'pos' | 'neg' | 'warn';
  last?: boolean;
}): ReactNode {
  const cls =
    tone === 'pos' ? 'text-pos'
    : tone === 'neg' ? 'text-neg'
    : tone === 'warn' ? 'text-warning'
    : 'text-text-primary';
  return (
    <div className={`flex flex-col items-start gap-1 px-3.5 py-3 ${last ? '' : 'border-r border-border-hairline'}`}>
      <div className="text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">{label}</div>
      <div className={`font-mono text-lg font-semibold leading-none ${cls}`}>{value}</div>
    </div>
  );
}

function HardwareNodeCard({
  node,
  fallbackIdx,
}: {
  node: {
    local_id: string;
    status: string;
    hardware?: readonly { type: string; count: number }[];
    poc_weight?: number;
    scaled_weight?: number | null;
    weight_scale_factor?: string;
  };
  fallbackIdx: number;
}): ReactNode {
  const { t } = useTranslation();
  const statusTone =
    node.status === 'FAILED' ? 'neg'
    : node.status === 'INFERENCE' ? 'link'
    : 'pos';
  const statusCls =
    statusTone === 'neg' ? 'bg-neg-tint text-neg'
    : statusTone === 'link' ? 'bg-link-tint text-link'
    : 'bg-pos-tint text-pos';

  return (
    <div className="rounded-xl bg-surface-2 px-3.5 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[13px] font-medium text-text-primary truncate">
          {node.local_id || `node-${fallbackIdx}`}
        </span>
        <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[1px] ${statusCls}`}>
          {node.status}
        </span>
      </div>
      {node.hardware && node.hardware.length > 0 && (
        <div className="mt-2 font-mono text-xs text-text-secondary">
          {node.hardware.map((h) => `${h.count}× ${h.type}`).join(' · ')}
        </div>
      )}
      {node.poc_weight != null && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-text-tertiary">
          <span className="font-semibold uppercase tracking-[1px]">{t('miner.weight')}</span>
          <span className="font-mono text-text-secondary">{formatWeight(node.poc_weight)}</span>
        </div>
      )}
      {node.scaled_weight != null && (
        <div className="mt-0.5 flex justify-between text-[11px]">
          <span className="text-text-tertiary">
            {t('miner.scaledWeight')}
            {node.weight_scale_factor ? ` ×${node.weight_scale_factor}` : ''}
          </span>
          <span className="font-mono tabular-nums text-text-secondary">
            {node.scaled_weight.toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
}

function RewardsTab({
  address,
  data,
  epochId,
  loadNextPage,
}: {
  address: string;
  data: { rewards: readonly RewardEntry[]; pagination: { has_next: boolean } } | null;
  epochId: number | null;
  loadNextPage: () => void;
}): ReactNode {
  const { t } = useTranslation();

  const rewards = data?.rewards ?? [];
  const today = rewards[0]?.assigned_reward ?? 0;
  const week = rewards.slice(0, 7).reduce((s, r) => s + r.assigned_reward, 0);
  const allTime = rewards.reduce((s, r) => s + r.assigned_reward, 0);

  const recent14 = [...rewards.slice(0, 14)].reverse().map((r) => r.assigned_reward);

  return (
    <div>
      {/* This epoch */}
      <div className="mb-3.5 rounded-2xl bg-surface-2 px-3.5 py-3.5">
        <div className="mb-1.5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
          <span>{t('miner.thisEpoch', { id: epochId ?? '—' })}</span>
        </div>
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="font-mono text-[28px] font-semibold leading-none tracking-[-0.02em] text-pos">
              +{today.toFixed(4)}
            </div>
            <div className="mt-1.5 text-[11px] text-text-tertiary">GNK</div>
          </div>
          {recent14.length > 1 && (
            <Sparkline values={recent14} color="var(--color-pos)" width={120} height={34} strokeWidth={1.4} currentMarker currentColor="var(--color-pos)" />
          )}
        </div>
      </div>

      {/* KPI row */}
      <div className="mb-4 grid grid-cols-3 gap-2">
        <Kpi label={t('miner.rewardsToday')} value={formatGNK(today, 2)} />
        <Kpi label={t('miner.rewards7d')} value={formatGNK(week, 2)} />
        <Kpi label={t('miner.rewardsAllTime')} value={formatGNK(allTime, 2)} />
      </div>

      <EpochHistoryList
        address={address}
        rewards={rewards}
        hasNext={data?.pagination.has_next ?? false}
        onLoadMore={loadNextPage}
      />
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="rounded-xl bg-surface-2 px-3 py-2.5">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">{label}</div>
      <div className="font-mono text-sm font-medium text-text-primary">{value}</div>
    </div>
  );
}

function formatGnk(value: number): string {
  if (value === 0) return '0';
  if (value >= 0.0001) return value.toFixed(4);
  return value.toFixed(9).replace(/0+$/, '');
}

function CollateralTabView({
  data,
}: {
  data: import('../../lib/minerApi.js').CollateralData | null;
}): ReactNode {
  const { t } = useTranslation();
  if (!data) {
    return <div className="py-6 text-center text-[11px] text-text-tertiary">—</div>;
  }

  // Prefer backend-computed coverage_pct (v3); fall back to local ratio when absent.
  const coveragePct =
    data.coverage_pct != null
      ? Math.round(data.coverage_pct)
      : data.required_gnk > 0
        ? Math.round((data.deposited_gnk / data.required_gnk) * 100)
        : 0;

  const hasV3Breakdown =
    data.potential_weight != null ||
    data.needed_collateral_gnk != null ||
    data.collateral_rate != null;

  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-surface-2 px-4 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
              {t('miner.bonded')}
            </div>
            <div className="mt-1 font-mono text-[28px] font-medium leading-none tracking-[-1px] tabular-nums text-text-primary">
              {formatGnk(data.deposited_gnk)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
              {t('miner.coverage')}
            </div>
            <div
              className={`mt-1 font-mono text-[24px] font-medium leading-none tabular-nums ${data.is_sufficient ? 'text-pos' : 'text-neg'}`}
            >
              {coveragePct}%
            </div>
          </div>
        </div>
        <div className="mt-3 h-[2px] overflow-hidden rounded-full bg-border-strong">
          <div
            className={data.is_sufficient ? 'h-full bg-[var(--color-pos)]' : 'h-full bg-[var(--color-neg)]'}
            style={{ width: `${Math.min(coveragePct, 100)}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <StatTile
          label={t('miner.deposited')}
          value={`${formatGnk(data.deposited_gnk)} GNK`}
        />
        <StatTile
          label={
            data.needed_collateral_gnk != null
              ? t('miner.neededCollateral')
              : t('miner.required')
          }
          value={`${formatGnk(data.needed_collateral_gnk ?? data.required_gnk)} GNK`}
        />
      </div>

      <div className="rounded-xl bg-surface-2 px-4 py-3">
        <div className="mb-2 text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
          {t('miner.weightBreakdown')}
        </div>
        <div className="space-y-2">
          {data.potential_weight != null && (
            <div className="flex justify-between text-[11px]">
              <span className="text-text-tertiary">{t('miner.potentialWeight')}</span>
              <span className="font-mono tabular-nums text-text-primary">
                {data.potential_weight.toLocaleString()}
              </span>
            </div>
          )}
          <div className="flex justify-between text-[11px]">
            <span className="text-text-tertiary">{t('miner.baseWeight')}</span>
            <span className="font-mono tabular-nums text-text-primary">
              {data.base_weight.toLocaleString()}
            </span>
          </div>
          {data.eligible_weight > 0 && (
            <div className="flex justify-between text-[11px]">
              <span className="text-text-tertiary">{t('miner.eligibleWeight')}</span>
              <span className="font-mono tabular-nums text-text-primary">
                {data.eligible_weight.toLocaleString()}
              </span>
            </div>
          )}
          <div className="flex justify-between text-[11px]">
            <span className="text-text-tertiary">{t('miner.activatedWeight')}</span>
            <span className="font-mono tabular-nums text-pos">
              {data.activated_weight.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between border-t border-border-hairline pt-2 text-[11px]">
            <span className="text-text-tertiary">{t('miner.effectiveWeight')}</span>
            <span className="font-mono font-medium tabular-nums text-text-primary">
              {data.effective_weight.toLocaleString()}
              <span
                className={`ml-1.5 text-[10px] ${data.weight_efficiency_pct >= 100 ? 'text-pos' : 'text-neg'}`}
              >
                {data.weight_efficiency_pct}%
              </span>
            </span>
          </div>
        </div>
      </div>

      {hasV3Breakdown && (
        <div className="rounded-xl bg-surface-2 px-4 py-3">
          <div className="mb-2 text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
            {t('miner.collateralBreakdown')}
          </div>
          <div className="space-y-2">
            {data.collateral_rate_pct != null && (
              <div className="flex justify-between text-[11px]">
                <span className="text-text-tertiary">{t('miner.collateralRate')}</span>
                <span
                  className={`font-mono tabular-nums ${data.is_sufficient ? 'text-pos' : 'text-neg'}`}
                >
                  {Math.round(data.collateral_rate_pct)}%
                </span>
              </div>
            )}
            {/* cpwu is required in v3 CollateralData type -- backend guarantees it whenever hasV3Breakdown=true */}
            <div className="flex justify-between text-[11px]">
              <span className="text-text-tertiary">{t('miner.cpwu')}</span>
              <span className="font-mono tabular-nums text-text-secondary">
                {data.collateral_per_weight_unit_ngonka}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <StatTile
          label={t('miner.invalidSlash')}
          value={`${(data.slash_fraction_invalid * 100).toFixed(0)}%`}
          tone="neg"
        />
        <StatTile
          label={t('miner.downtimeSlash')}
          value={`${(data.slash_fraction_downtime * 100).toFixed(0)}%`}
          tone="warning"
        />
      </div>

      {data.history.length > 0 && (
        <div className="rounded-xl bg-surface-2 overflow-hidden">
          <div className="grid grid-cols-4 gap-1 px-3 py-2 text-[9px] font-semibold uppercase tracking-[1px] text-text-tertiary border-b border-border-hairline">
            <span>{t('miner.epoch')}</span>
            <span>{t('miner.deposited')}</span>
            <span>{t('miner.coverage')}</span>
            <span>{t('miner.slashed')}</span>
          </div>
          {data.history.slice(0, 10).map((h) => (
            <div
              key={h.epoch_id}
              className="grid grid-cols-4 gap-1 px-3 py-2 text-[10px] border-b border-border-hairline last:border-0"
            >
              <span className="text-text-tertiary">{h.epoch_id}</span>
              <span className="font-mono tabular-nums text-text-primary">{formatGnk(h.deposited_gnk)}</span>
              <span className={h.coverage_pct >= 100 ? 'text-pos' : 'text-neg'}>
                {h.coverage_pct >= 100 ? 'OK' : `${Math.round(h.coverage_pct)}%`}
              </span>
              <span className={h.slashed_gnk > 0 ? 'text-neg' : 'text-text-tertiary'}>
                {h.slashed_gnk > 0 ? h.slashed_gnk.toFixed(4) : '--'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
